// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

// Symbol for an ISO 4217 currency code. Derived via the platform `Intl` API so
// every currency the selector offers renders its own symbol (JPY → ¥, INR → ₹,
// BRL → R$, …) instead of a hard-coded €. Memoised per code — the money-field
// render path calls this once per field per render.
const symbolCache = new Map<string, string>();

export function currencySymbol(code?: unknown): string {
  // No / unset / non-string selection → the wizard's default currency symbol.
  if (typeof code !== 'string' || code.trim() === '') return '€';
  const key = code.trim().toUpperCase();
  const cached = symbolCache.get(key);
  if (cached !== undefined) return cached;
  let symbol: string;
  try {
    symbol =
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: key,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? key;
  } catch {
    // Not a valid ISO 4217 code → best-effort: show the code itself.
    symbol = key;
  }
  symbolCache.set(key, symbol);
  return symbol;
}
